// src/pages/finances/InvoiceDetail.jsx
import {
  CheckCircle as ApproveIcon,
  ArrowBack as ArrowBackIcon,
  AccountBalance as BankIcon,
  Cancel as CancelIcon,
  CreditCard as CardIcon,
  AttachMoney as CashIcon,
  AccessTime as ClockIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Receipt as InvoiceIcon,
  MoreVert as MoreVertIcon,
  Payment as PaymentIcon,
  Send as SendIcon,
  MoneyOff as WriteOffIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Import custom components
import Breadcrumbs from '../../components/core/Breadcrumbs';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { EntityStatusBadge } from '../../components/core';
import { Card } from '../../design-system/components/core';
import TabPanel from '../../components/ui/TabPanel';
import PaymentForm from './PaymentForm';

// Import stores
import { Chip } from '@mui/material';
import { MagicLoading } from '../../components/ui';
import useAuthStore from '../../stores/authStore';
import useInvoiceStore from '../../stores/invoiceStore';

const InvoiceDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  // Get user permissions
  const { hasPermission } = useAuthStore();
  const canUpdate = hasPermission('finances', 'update');
  const canDelete = hasPermission('finances', 'delete');
  
  // Get invoice from store
  const { 
    currentInvoice, 
    isLoading, 
    error, 
    fetchInvoiceById, 
    deleteInvoice,
    sendInvoice,
    generateInvoicePdf,
    addPayment,
    cancelInvoice,
    approveInvoice
  } = useInvoiceStore();
  
  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  
  // Fetch invoice on mount
  useEffect(() => {
    fetchInvoiceById(id);
    
    // Check if a specific tab is requested in the URL
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab === 'payments') {
      setTabValue(1);
    }
  }, [id, fetchInvoiceById]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle action menu open
  const handleActionMenuOpen = (event) => {
    setActionAnchorEl(event.currentTarget);
  };
  
  // Handle action menu close
  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
  };
  
  // Handle invoice action
  const handleInvoiceAction = (action) => {
    handleActionMenuClose();
    
    switch (action) {
      case 'edit':
        navigate(`/invoices/${id}/edit`);
        break;
      case 'delete':
        setConfirmAction('delete');
        setConfirmDialogOpen(true);
        break;
      case 'send':
        setConfirmAction('send');
        setConfirmDialogOpen(true);
        break;
      case 'download':
        generateInvoicePdf(id);
        break;
      case 'cancel':
        setConfirmAction('cancel');
        setConfirmDialogOpen(true);
        break;
      case 'payment':
        setPaymentDialogOpen(true);
        break;
      case 'approve':
        setConfirmAction('approve');
        setConfirmDialogOpen(true);
        break;
      case 'writeoff':
        setConfirmAction('writeoff');
        setConfirmDialogOpen(true);
        break;
      default:
        break;
    }
  };
  
  // Handle confirm dialog action
  const handleConfirmAction = async () => {
    try {
      switch (confirmAction) {
        case 'delete':
          await deleteInvoice(id);
          navigate('/invoices');
          return;
        case 'send':
          await sendInvoice(id, { recipient: currentInvoice.billing?.contactEmail });
          break;
        case 'cancel':
          await cancelInvoice(id, "Cancelled by user");
          break;
        case 'approve':
          await approveInvoice(id);
          break;
        case 'writeoff':
          // Use cancelInvoice with writeoff reason
          await cancelInvoice(id, "Written off by user", "writeoff");
          break;
        default:
          break;
      }
      
      setConfirmDialogOpen(false);
      // Refresh the invoice to get the updated status
      fetchInvoiceById(id);
    } catch (error) {
      console.log('Error performing action:', error);
      // You could show an error message here
    }
  };
  
  // Handle payment submit
  const handlePaymentSubmit = async (paymentData) => {
    try {
      await addPayment(id, paymentData);
      setPaymentDialogOpen(false);
      // Refresh the invoice to show the new payment
      fetchInvoiceById(id);
    } catch (error) {
      console.log('Error adding payment:', error);
      // You could show an error message here
    }
  };
  
  // Format money
  const formatMoney = (amount, currency = "USD") => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM d, yyyy');
  };
  
  // Get payment method icon
  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'bank_transfer':
        return <BankIcon fontSize="small" />;
      case 'credit_card':
        return <CardIcon fontSize="small" />;
      case 'cash':
        return <CashIcon fontSize="small" />;
      default:
        return <PaymentIcon fontSize="small" />;
    }
  };
  
  // Determine if invoice is overdue
  const isOverdue = () => {
    if (!currentInvoice || !currentInvoice.dates?.dueDate) return false;
    if (currentInvoice.status === 'paid' || currentInvoice.status === 'cancelled') return false;
    
    return new Date(currentInvoice.dates.dueDate) < new Date();
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        current={currentInvoice ? `Invoice #${currentInvoice.invoiceNumber}` : 'Invoice Details'}
        parentLink="/invoices"
        parentLabel="Invoices"
      />
      
      {/* Header section with back button and actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate('/invoices')}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            {currentInvoice ? `Invoice #${currentInvoice.invoiceNumber}` : 'Invoice Details'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canUpdate && (
            <Button
              variant="outlined"
              startIcon={<MoreVertIcon />}
              onClick={handleActionMenuOpen}
            >
              Actions
            </Button>
          )}
          
          {canUpdate && currentInvoice && !['paid', 'cancelled', 'refunded', 'writeoff'].includes(currentInvoice.status) && (
            <Button
              variant="contained"
              
              startIcon={<PaymentIcon />}
              onClick={() => handleInvoiceAction('payment')}
            >
              Record Payment
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Loading indicator */}
      {isLoading && <MagicLoading />}
      
      {/* Error message */}
      {error && (
        <Card level={1} sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}
      
      {/* Invoice details */}
      {!isLoading && !error && currentInvoice && (
        <>
          {/* Status banner if overdue */}
          {isOverdue() && (
            <Card 
              level={1} 
              sx={{ 
                mb: 3, 
                p: 2, 
                bgcolor: alpha(theme.palette.error.main, 0.1),
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ClockIcon color="error" sx={{ mr: 1 }} />
                <Typography color="error" variant="subtitle2">
                  This invoice is overdue by {Math.round((new Date() - new Date(currentInvoice.dates.dueDate)) / (1000 * 60 * 60 * 24))} days
                </Typography>
              </Box>
              
              {canUpdate && (
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small"
                  onClick={() => handleInvoiceAction('payment')}
                >
                  Record Payment
                </Button>
              )}
            </Card>
          )}
          
          {/* Summary card */}
          <Card level={2} sx={{ mb: 3 }}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="overline" color="textSecondary">Client</Typography>
                      <Typography>
                        {typeof currentInvoice.clientId === 'object' 
                          ? currentInvoice.clientId?.name 
                          : 'Client #' + currentInvoice.clientId}
                      </Typography>
                      
                      {currentInvoice.billing && (
                        <Box sx={{ mt: 1 }}>
                          {currentInvoice.billing.contactName && (
                            <Typography variant="body2">
                              {currentInvoice.billing.contactName}
                            </Typography>
                          )}
                          {currentInvoice.billing.contactEmail && (
                            <Typography variant="body2">
                              {currentInvoice.billing.contactEmail}
                            </Typography>
                          )}
                          {currentInvoice.billing.address && (
                            <Typography variant="body2">
                              {currentInvoice.billing.address.street}, {currentInvoice.billing.address.city}
                              {currentInvoice.billing.address.state && `, ${currentInvoice.billing.address.state}`}
                              {currentInvoice.billing.address.zipCode && ` ${currentInvoice.billing.address.zipCode}`}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    <EntityStatusBadge status={currentInvoice.status} entityType="invoice" />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">Invoice Date</Typography>
                      <Typography variant="body1">{formatDate(currentInvoice.dates?.issueDate)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">Due Date</Typography>
                      <Typography variant="body1">{formatDate(currentInvoice.dates?.dueDate)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">Invoice #</Typography>
                      <Typography variant="body1">{currentInvoice.invoiceNumber}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">Total Amount</Typography>
                      <Typography variant="h6"  color={isOverdue() ? 'error.main' : 'inherit'}>
                        {formatMoney(currentInvoice.amount?.total, currentInvoice.currency?.code)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Card>
          
          {/* Tabs section */}
          <Box sx={{ width: '100%', mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Details" />
              <Tab label="Payments" />
              <Tab label="Activity" />
            </Tabs>
            
            {/* Details Tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                {/* Items */}
                <Grid item xs={12}>
                  <Card level={1}>
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>Invoice Items</Typography>
                      
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Description</TableCell>
                              <TableCell align="right">Quantity</TableCell>
                              <TableCell align="right">Unit Price</TableCell>
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentInvoice.items?.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell sx={{ maxWidth: '50%' }}>
                                  <Typography variant="body2">{item.description}</Typography>
                                  {item.itemType && (
                                    <Typography variant="caption" color="textSecondary">
                                      Type: {item.itemType}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">
                                  {formatMoney(item.unitPrice, currentInvoice.currency?.code)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatMoney(item.amount, currentInvoice.currency?.code)}
                                </TableCell>
                              </TableRow>
                            ))}
                            
                            {/* Subtotal */}
                            <TableRow>
                              <TableCell colSpan={3} align="right" sx={{ border: 0 }}>
                                <Typography variant="subtitle2">Subtotal</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ border: 0 }}>
                                <Typography variant="subtitle2">
                                  {formatMoney(currentInvoice.amount?.subtotal, currentInvoice.currency?.code)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            
                            {/* Tax */}
                            {currentInvoice.amount?.taxTotal > 0 && (
                              <TableRow>
                                <TableCell colSpan={3} align="right" sx={{ border: 0 }}>
                                  <Typography variant="subtitle2">Tax</Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ border: 0 }}>
                                  <Typography variant="subtitle2">
                                    {formatMoney(currentInvoice.amount?.taxTotal, currentInvoice.currency?.code)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {/* Discount */}
                            {currentInvoice.amount?.discountTotal > 0 && (
                              <TableRow>
                                <TableCell colSpan={3} align="right" sx={{ border: 0 }}>
                                  <Typography variant="subtitle2">Discount</Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ border: 0 }}>
                                  <Typography variant="subtitle2">
                                    - {formatMoney(currentInvoice.amount?.discountTotal, currentInvoice.currency?.code)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {/* Total */}
                            <TableRow>
                              <TableCell colSpan={3} align="right" sx={{ border: 0 }}>
                                <Typography variant="h6">Total</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ border: 0 }}>
                                <Typography variant="h6">
                                  {formatMoney(currentInvoice.amount?.total, currentInvoice.currency?.code)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            
                            {/* Amount paid */}
                            <TableRow>
                              <TableCell colSpan={3} align="right" sx={{ border: 0 }}>
                                <Typography variant="subtitle2">Amount Paid</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ border: 0 }}>
                                <Typography variant="subtitle2" color="success.main">
                                  {formatMoney(currentInvoice.amount?.paid, currentInvoice.currency?.code)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            
                            {/* Amount due */}
                            <TableRow>
                              <TableCell colSpan={3} align="right" sx={{ border: 0 }}>
                                <Typography variant="subtitle2">Amount Due</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ border: 0 }}>
                                <Typography 
                                  variant="subtitle2"
                                  color={
                                    currentInvoice.amount?.due > 0 && isOverdue() 
                                      ? 'error.main' 
                                      : currentInvoice.amount?.due > 0 
                                        ? 'warning.main' 
                                        : 'success.main'
                                  }
                                >
                                  {formatMoney(currentInvoice.amount?.due, currentInvoice.currency?.code)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Card>
                </Grid>
                
                {/* Notes and Terms */}
                <Grid item xs={12} md={6}>
                  <Card level={1} sx={{ height: '100%' }}>
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>Notes</Typography>
                      <Typography variant="body2">
                        {currentInvoice.notes || 'No notes available.'}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card level={1} sx={{ height: '100%' }}>
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>Terms</Typography>
                      <Typography variant="body2">
                        {currentInvoice.terms || 'No terms specified.'}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Payments Tab */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card level={1}>
                    <Box sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Payment History</Typography>
                        
                        {canUpdate && currentInvoice && !['paid', 'cancelled', 'refunded', 'writeoff'].includes(currentInvoice.status) && (
                          <Button
                            variant="contained"
                            
                            startIcon={<PaymentIcon />}
                            onClick={() => handleInvoiceAction('payment')}
                          >
                            Record Payment
                          </Button>
                        )}
                      </Box>
                      
                      {currentInvoice.payments && currentInvoice.payments.length > 0 ? (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Notes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {currentInvoice.payments.map((payment, index) => (
                                <TableRow key={index}>
                                  <TableCell>{formatDate(payment.date)}</TableCell>
                                  <TableCell>
                                    {formatMoney(payment.amount, currentInvoice.currency?.code)}
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {getPaymentMethodIcon(payment.method)}
                                      <Typography variant="body2" sx={{ ml: 1 }}>
                                        {payment.method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small"
                                      label={payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                                      color={
                                        payment.status === 'completed' 
                                          ? 'success' 
                                          : payment.status === 'pending' 
                                            ? 'warning' 
                                            : payment.status === 'failed'
                                              ? 'error'
                                              : 'default'
                                      }
                                      variant="filled"
                                    />
                                  </TableCell>
                                  <TableCell>{payment.notes || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="textSecondary">
                            No payments recorded yet.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
                
                {/* Payment summary */}
                <Grid item xs={12} md={6}>
                  <Card level={1}>
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>Payment Summary</Typography>
                      
                      <List disablePadding>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText primary="Total Amount" />
                          <Typography variant="body1">
                            {formatMoney(currentInvoice.amount?.total, currentInvoice.currency?.code)}
                          </Typography>
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText primary="Amount Paid" />
                          <Typography variant="body1" color="success.main">
                            {formatMoney(currentInvoice.amount?.paid, currentInvoice.currency?.code)}
                          </Typography>
                        </ListItem>
                        <Divider sx={{ my: 1 }} />
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText primary="Amount Due" />
                          <Typography 
                            variant="body1" 
                            fontWeight="bold"
                            color={
                              currentInvoice.amount?.due > 0 && isOverdue() 
                                ? 'error.main' 
                                : currentInvoice.amount?.due > 0 
                                  ? 'warning.main' 
                                  : 'success.main'
                            }
                          >
                            {formatMoney(currentInvoice.amount?.due, currentInvoice.currency?.code)}
                          </Typography>
                        </ListItem>
                      </List>
                    </Box>
                  </Card>
                </Grid>
                
                {/* Payment instructions */}
                <Grid item xs={12} md={6}>
                  <Card level={1}>
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>Payment Instructions</Typography>
                      
                      <Typography variant="body2" paragraph>
                        Please use the following payment methods:
                      </Typography>
                      
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <BankIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Bank Transfer" 
                            secondary="Include invoice number as reference"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CardIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Credit Card" 
                            secondary={
                              currentInvoice.onlinePayment?.enabled 
                                ? "Use the payment link provided in the email"
                                : "Contact us to pay by card"
                            }
                          />
                        </ListItem>
                      </List>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Activity Tab */}
            <TabPanel value={tabValue} index={2}>
              <Card level={1}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Invoice Activity</Typography>
                  
                  {/* Format the reminders and history here */}
                  <List>
                    {/* Creation */}
                    <ListItem>
                      <ListItemIcon>
                        <InvoiceIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Invoice Created" 
                        secondary={formatDate(currentInvoice.createdAt)}
                      />
                    </ListItem>
                    
                    {/* If sent */}
                    {currentInvoice.dates?.sentDate && (
                      <ListItem>
                        <ListItemIcon>
                          <SendIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Invoice Sent" 
                          secondary={formatDate(currentInvoice.dates.sentDate)}
                        />
                      </ListItem>
                    )}
                    
                    {/* If paid */}
                    {currentInvoice.dates?.paidDate && (
                      <ListItem>
                        <ListItemIcon>
                          <PaymentIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Invoice Paid" 
                          secondary={formatDate(currentInvoice.dates.paidDate)}
                        />
                      </ListItem>
                    )}
                    
                    {/* Reminders */}
                    {currentInvoice.reminders?.map((reminder, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <SendIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={`Reminder Sent (${reminder.type})`}
                          secondary={formatDate(reminder.sentAt)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Card>
            </TabPanel>
          </Box>
          
          {/* Actions menu */}
          <Menu
            anchorEl={actionAnchorEl}
            open={Boolean(actionAnchorEl)}
            onClose={handleActionMenuClose}
          >
            {canUpdate && (
              <MenuItem onClick={() => handleInvoiceAction('edit')}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Invoice</ListItemText>
              </MenuItem>
            )}
            
            <MenuItem onClick={() => handleInvoiceAction('download')}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download PDF</ListItemText>
            </MenuItem>
            
            {currentInvoice && ['draft', 'pending'].includes(currentInvoice.status) && canUpdate && (
              <MenuItem onClick={() => handleInvoiceAction('send')}>
                <ListItemIcon>
                  <SendIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Send to Client</ListItemText>
              </MenuItem>
            )}
            
            {currentInvoice && currentInvoice.status === 'pending' && canUpdate && (
              <MenuItem onClick={() => handleInvoiceAction('approve')}>
                <ListItemIcon>
                  <ApproveIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText>Approve Invoice</ListItemText>
              </MenuItem>
            )}
            
            {currentInvoice && !['paid', 'cancelled', 'refunded', 'writeoff'].includes(currentInvoice.status) && canUpdate && (
              <MenuItem onClick={() => handleInvoiceAction('payment')}>
                <ListItemIcon>
                  <PaymentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Record Payment</ListItemText>
              </MenuItem>
            )}
            
            {currentInvoice && !['cancelled', 'refunded', 'writeoff'].includes(currentInvoice.status) && canUpdate && (
              <MenuItem onClick={() => handleInvoiceAction('cancel')}>
                <ListItemIcon>
                  <CancelIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cancel Invoice</ListItemText>
              </MenuItem>
            )}
            
            {currentInvoice && !['paid', 'cancelled', 'refunded', 'writeoff'].includes(currentInvoice.status) && canUpdate && (
              <MenuItem onClick={() => handleInvoiceAction('writeoff')}>
                <ListItemIcon>
                  <WriteOffIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Write Off Invoice</ListItemText>
              </MenuItem>
            )}
            
            {canDelete && (
              <>
                <Divider />
                <MenuItem onClick={() => handleInvoiceAction('delete')} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Delete Invoice</ListItemText>
                </MenuItem>
              </>
            )}
          </Menu>
          
          {/* Confirmation dialog */}
          <ConfirmDialog
            open={confirmDialogOpen}
            title={
              confirmAction === 'delete' ? 'Delete Invoice' :
              confirmAction === 'send' ? 'Send Invoice' :
              confirmAction === 'cancel' ? 'Cancel Invoice' :
              confirmAction === 'approve' ? 'Approve Invoice' :
              confirmAction === 'writeoff' ? 'Write Off Invoice' : 
              'Confirm Action'
            }
            content={
              confirmAction === 'delete' ? 'Are you sure you want to delete this invoice? This action cannot be undone.' :
              confirmAction === 'send' ? `Are you sure you want to send this invoice to ${currentInvoice.billing?.contactEmail || 'the client'}?` :
              confirmAction === 'cancel' ? 'Are you sure you want to cancel this invoice? This will mark it as cancelled and notify the client.' :
              confirmAction === 'approve' ? 'Are you sure you want to approve this invoice? This will mark it as ready to be sent to the client.' :
              confirmAction === 'writeoff' ? 'Are you sure you want to write off this invoice? This will mark it as written off and no further payments will be expected.' :
              'Are you sure you want to perform this action?'
            }
            confirmText={
              confirmAction === 'delete' ? 'Delete' :
              confirmAction === 'send' ? 'Send' :
              confirmAction === 'cancel' ? 'Cancel Invoice' :
              confirmAction === 'approve' ? 'Approve' :
              confirmAction === 'writeoff' ? 'Write Off' :
              'Confirm'
            }
            confirmButtonColor={
              confirmAction === 'delete' || confirmAction === 'cancel' || confirmAction === 'writeoff' 
                ? 'error' 
                : 'primary'
            }
            isDelete={confirmAction === 'delete'}
            onConfirm={handleConfirmAction}
            onCancel={() => {
              setConfirmDialogOpen(false);
            }}
          />
          
          {/* Payment dialog */}
          <PaymentForm
            open={paymentDialogOpen}
            onClose={() => setPaymentDialogOpen(false)}
            onSubmit={handlePaymentSubmit}
            invoice={currentInvoice}
          />
        </>
      )}
    </Box>
  );
};

export default InvoiceDetail;






